import { useCallback } from 'react'
import { useAuthStore } from '../store/authStore'
import { useEnumStore } from '../store/enumStore'

/**
 * Get localized label for an enum code
 * @param enumName - The name of the enum (e.g., 'AccountStatus', 'AccountType')
 * @param code - The enum code value (e.g., 'ACTIVE', 'SHIPPER')
 * @returns The localized label or the code if not found
 */
export function getLocalizedLabel(enumName: string, code: string | null | undefined): string {
  if (!code) return '-'

  const { getLocalizedLabel } = useEnumStore.getState()
  return getLocalizedLabel(enumName, code)
}

/**
 * Hook version for use in React components.
 * Re-renders when enum cache or the user's server locale changes.
 */
export function useLocalizedLabel() {
  const userLocale = useAuthStore((s) => s.user?.locale)
  const enumMap = useEnumStore((s) => s.enumMap)
  const loadedLocale = useEnumStore((s) => s.loadedLocale)
  const getLocalizedLabelFromStore = useEnumStore((s) => s.getLocalizedLabel)

  return useCallback(
    (enumName: string, code: string | null | undefined): string => {
      if (!code) return '-'
      return getLocalizedLabelFromStore(enumName, code)
    },
    [userLocale, enumMap, loadedLocale, getLocalizedLabelFromStore],
  )
}

/** Hook for enum filter/dropdown options. */
export function useEnumOptions() {
  const userLocale = useAuthStore((s) => s.user?.locale)
  const enumMap = useEnumStore((s) => s.enumMap)
  const loadedLocale = useEnumStore((s) => s.loadedLocale)
  const getEnumOptions = useEnumStore((s) => s.getEnumOptions)

  return useCallback(
    (enumName: string) => getEnumOptions(enumName),
    [userLocale, enumMap, loadedLocale, getEnumOptions],
  )
}
